import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ClientDetail.css';
import PaymentSchedule from './PaymentSchedule';
import { Pencil, Save, Key, ShieldCheck, AlertTriangle, Copy, ExternalLink, Paperclip, Trash2, CreditCard, FileDown, MessageSquare, FileText, PenLine, Image, Music, Video, XCircle, CheckCircle, User, Phone, Mail, Coins, Calendar, RefreshCw, Plus, Eye, Building2, MapPin } from 'lucide-react';

function ClientDetail({ client, onClose, onUpdate, isAdmin, isCommercial, isClientView }) {
  const [internalNotes, setInternalNotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newInternalNote, setNewInternalNote] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittingMessage, setSubmittingMessage] = useState(false);
  const [editingClient, setEditingClient] = useState(false);
  const [clientData, setClientData] = useState(client);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [generatingBonCommande, setGeneratingBonCommande] = useState(false);
  const [factures, setFactures] = useState([]);
  const [souscriptions, setSouscriptions] = useState([]);
  const [loadingSouscriptions, setLoadingSouscriptions] = useState(true);
  const [opLoading, setOpLoading] = useState(null);   // `${souscriptionId}:${type}` en cours
  const [opResult, setOpResult] = useState(null);     // { souscriptionId, title, ok, message }
  const [autoRun, setAutoRun] = useState(null);       // résultat du lancement global
  const [autoRunning, setAutoRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [stripeLinks, setStripeLinks] = useState([]);
  const [loadingStripeLinks, setLoadingStripeLinks] = useState(true);
  const [paymentStats, setPaymentStats] = useState(null);
  const [showStripeLinks, setShowStripeLinks] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [markingAsPaid, setMarkingAsPaid] = useState(false);
  const [showStripeForm, setShowStripeForm] = useState(false);

  // Monetico Payment Link state
  const [showMoneticoModal, setShowMoneticoModal] = useState(false);
  const [moneticoType, setMoneticoType] = useState('immediat');
  const [moneticoAmount, setMoneticoAmount] = useState('');
  const [moneticoDossier, setMoneticoDossier] = useState('');
  const [moneticoDescription, setMoneticoDescription] = useState('');
  const [moneticoGenerating, setMoneticoGenerating] = useState(false);
  const [moneticoLink, setMoneticoLink] = useState(null);
  const [moneticoCopied, setMoneticoCopied] = useState(false);
  const [moneticoSending, setMoneticoSending] = useState(null); // 'email' | 'sms' | null
  const [moneticoSent, setMoneticoSent] = useState(null);       // 'email' | 'sms' | null
  const [stripeAmount, setStripeAmount] = useState('');
  const [generatingStripeLink, setGeneratingStripeLink] = useState(false);
  const [generatedStripeLink, setGeneratedStripeLink] = useState(null);
  const [showSignatureForm, setShowSignatureForm] = useState(false);
  const [signatureCode, setSignatureCode] = useState('');
  const [sendingSignatureCode, setSendingSignatureCode] = useState(false);
  const [validatingSignatureCode, setValidatingSignatureCode] = useState(false);

  useEffect(() => {
    // Si c'est un client qui regarde sa propre fiche, charger fichiers, factures et messages
    if (isClientView) {
      fetchFiles();
      fetchFactures();
      fetchMessages();
    } else {
      // Pour les admins/agents, charger tout
      fetchInternalNotes();
      fetchMessages();
      fetchFiles();
      fetchFactures();
      fetchSouscriptions();
      fetchHistory();
      fetchStripeLinks();
      fetchPaymentStats();
    }
    setLoading(false);
  }, [client.id]);

  const fetchInternalNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/clients/${client.id}/internal-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInternalNotes(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des notes internes:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/clients/${client.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
    }
  };

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/clients/${client.id}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des fichiers:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const fetchFactures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/clients/${client.id}/factures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFactures(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des factures:', error);
    }
  };

  const fetchSouscriptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/clients/${client.id}/souscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSouscriptions(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des souscriptions:', error);
    } finally {
      setLoadingSouscriptions(false);
    }
  };

  // Opérations Monetico sur une souscription (admin) : capture / stop / refund / state
  const runSouscriptionOp = async (s, type) => {
    const titles = { capture: 'Mise en recouvrement', stop: 'Résiliation de la récurrence', refund: 'Remboursement', state: "État du paiement" };
    const montant = parseFloat(s.montant || 0);
    let refundAmount;
    let captureAmount;

    if (type === 'capture') {
      const input = window.prompt(`Montant à recouvrer (1 à ${montant.toFixed(2)} ${s.devise || 'EUR'}) — laisse le total ou réduis pour un geste commercial :`, montant.toFixed(2));
      if (input == null) return;
      captureAmount = parseFloat(String(input).replace(',', '.'));
      if (!(captureAmount > 0) || captureAmount > montant) { alert('Montant invalide.'); return; }
      if (!window.confirm(`Recouvrer ${captureAmount.toFixed(2)} ${s.devise || 'EUR'}${captureAmount < montant ? ` (au lieu de ${montant.toFixed(2)})` : ''} ?\n\n⚠️ Cette opération débite réellement la carte.`)) return;
    }
    if (type === 'stop' && !window.confirm("Arrêter définitivement la récurrence de cet abonnement ?\nLes prélèvements futurs seront annulés.")) return;
    if (type === 'refund') {
      const input = window.prompt(`Montant à rembourser (max ${montant.toFixed(2)} ${s.devise || 'EUR'}) :`, montant.toFixed(2));
      if (input == null) return;
      refundAmount = parseFloat(String(input).replace(',', '.'));
      if (!(refundAmount > 0) || refundAmount > montant) { alert('Montant invalide.'); return; }
      if (!window.confirm(`Rembourser ${refundAmount.toFixed(2)} ${s.devise || 'EUR'} ?\n\n⚠️ Opération réelle et irréversible.`)) return;
    }

    setOpLoading(`${s.id}:${type}`);
    setOpResult(null);
    try {
      const token = localStorage.getItem('token');
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      let res;
      if (type === 'state')        res = await axios.get(`/api/souscriptions/${s.id}/state`, auth);
      else if (type === 'capture') res = await axios.post(`/api/souscriptions/${s.id}/capture`, { montant_a_capturer: captureAmount }, auth);
      else if (type === 'stop')    res = await axios.post(`/api/souscriptions/${s.id}/stop-recurrence`, {}, auth);
      else if (type === 'refund')  res = await axios.post(`/api/souscriptions/${s.id}/refund`, { montant: refundAmount }, auth);
      const d = res.data || {};

      let message;
      if (type === 'state') {
        const etatLabels = {
          EN: 'Carte non saisie par le client', AU: 'Autorisé, en attente de recouvrement',
          RE: 'Autorisation refusée', GR: 'Bloqué (tentatives max atteintes)', AN: 'Annulé',
          PA: 'Recouvré (payé)', PP: 'Partiel accepté', PF: 'Fractionné accepté',
          PR: 'Récurrent accepté', TR: 'En cours de traitement'
        };
        message = `État : ${d.etat || '—'}${d.etat && etatLabels[d.etat] ? ' — ' + etatLabels[d.etat] : ''}`
          + (d.montantrecouvre ? `\nDéjà recouvré : ${d.montantrecouvre}` : '')
          + (d.numauto ? `\nN° autorisation : ${d.numauto}` : '')
          + (d.motifrefus && d.motifrefus !== '-' ? `\nMotif de refus : ${d.motifrefus}` : '');
      } else {
        message = (d.lib || (d.success ? 'Opération acceptée' : 'Opération refusée'))
          + (d.aut ? `\nN° autorisation : ${d.aut}` : '')
          + (d.cdr != null ? `\n(code retour Monetico : ${d.cdr})` : '');
      }
      setOpResult({ souscriptionId: s.id, title: titles[type], ok: !!d.success, message });
      if (d.success && type !== 'state') fetchSouscriptions();
    } catch (e) {
      setOpResult({ souscriptionId: s.id, title: titles[type], ok: false, message: e.response?.data?.error || "Erreur lors de l'opération" });
    } finally {
      setOpLoading(null);
    }
  };

  // Active/désactive l'auto-recouvrement d'une souscription refusée
  const toggleAutoRecouvrement = async (s) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`/api/souscriptions/${s.id}/auto-recouvrement`,
        { active: !(s.auto_recouvrement && !s.recouvrement_stop_reason) },
        { headers: { Authorization: `Bearer ${token}` } });
      setSouscriptions(prev => prev.map(x => x.id === s.id
        ? { ...x, auto_recouvrement: res.data.auto_recouvrement, recouvrement_stop_reason: res.data.auto_recouvrement ? null : 'desactive_manuel' }
        : x));
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la modification');
    }
  };

  // Programme/annule une tentative réduite (à partir du jour N, tenter X €)
  const setReducedAttempt = async (s) => {
    const montant = parseFloat(s.montant || 0);
    const jourInput = window.prompt(`À partir de quel jour tenter un montant réduit ? (0 à 30)\nLaisse vide pour ANNULER la tentative réduite.`,
      s.recouvrement_jour_reduit != null ? String(s.recouvrement_jour_reduit) : '27');
    if (jourInput === null) return;
    const token = localStorage.getItem('token');
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      if (jourInput.trim() === '') {
        await axios.patch(`/api/souscriptions/${s.id}/recouvrement-reduit`, { jour: null, montant: null }, auth);
        setSouscriptions(prev => prev.map(x => x.id === s.id ? { ...x, recouvrement_jour_reduit: null, recouvrement_montant_reduit: null } : x));
        return;
      }
      const jour = parseInt(jourInput, 10);
      const montInput = window.prompt(`Montant réduit à tenter à partir du jour ${jour} (1 à ${montant.toFixed(2)}) :`,
        s.recouvrement_montant_reduit != null ? String(s.recouvrement_montant_reduit) : '1');
      if (montInput === null) return;
      const mont = parseFloat(String(montInput).replace(',', '.'));
      if (!(jour >= 0) || !(mont > 0) || mont > montant) { alert('Valeurs invalides.'); return; }
      await axios.patch(`/api/souscriptions/${s.id}/recouvrement-reduit`, { jour, montant: mont }, auth);
      setSouscriptions(prev => prev.map(x => x.id === s.id ? { ...x, recouvrement_jour_reduit: jour, recouvrement_montant_reduit: mont } : x));
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la programmation');
    }
  };

  // Lance la relance auto immédiatement (tous les clients, pas seulement celui-ci)
  const runAutoRecouvrementNow = async () => {
    if (!window.confirm("Lancer maintenant la relance de recouvrement de TOUS les paiements récurrents refusés (tous clients) ?\n\n⚠️ Chaque relance tente un débit réel.")) return;
    setAutoRunning(true);
    setAutoRun(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/souscriptions/auto-recouvrement/run', {},
        { headers: { Authorization: `Bearer ${token}` } });
      setAutoRun(res.data);
      fetchSouscriptions();
    } catch (e) {
      setAutoRun({ error: e.response?.data?.error || 'Erreur lors du lancement' });
    } finally {
      setAutoRunning(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/clients/${client.id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchStripeLinks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/clients/${client.id}/stripe-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeLinks(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des liens Stripe:', error);
    } finally {
      setLoadingStripeLinks(false);
    }
  };

  const fetchPaymentStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/clients/${client.id}/stripe-payment-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des stats de paiement:', error);
    }
  };

  const handleGenerateInvoice = async () => {
    const today = new Date().toISOString().split('T')[0];
    const datePaiement = window.prompt(
      'Date à laquelle le client a payé (la facture sera émise à cette date) :\nFormat AAAA-MM-JJ',
      today
    );
    if (!datePaiement) return;

    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePaiement)) {
      alert('Format de date invalide. Utilisez AAAA-MM-JJ (ex: 2026-04-15)');
      return;
    }

    const modePaiement = window.prompt(
      'Mode de paiement (virement, cb, especes, cheque, prelevement, autre) :',
      'virement'
    );
    if (!modePaiement) return;

    setGeneratingInvoice(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/clients/${client.id}/facture`,
        {
          date_paiement: datePaiement,
          mode_paiement: modePaiement,
          reference_paiement: ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Facture ${response.data.numeroFacture} générée avec succès!`);

      // Télécharger automatiquement la facture
      const pdfResponse = await axios.get(
        `/api/factures/by-numero/${response.data.numeroFacture}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${response.data.numeroFacture}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      fetchFactures(); // Rafraîchir la liste des factures
    } catch (error) {
      console.error('Erreur lors de la génération de la facture:', error);
      alert(error.response?.data?.error || 'Erreur lors de la génération de la facture');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleGenerateBonCommande = async () => {
    if (!window.confirm('Voulez-vous générer un bon de commande pour ce client ?')) {
      return;
    }

    setGeneratingBonCommande(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/clients/${client.id}/bon-commande`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Bon de commande ${response.data.numeroBDC} généré avec succès!`);

      // Télécharger automatiquement le PDF
      const pdfResponse = await axios.get(
        `/api/bons-commande/${response.data.numeroBDC}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${response.data.numeroBDC}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Recharger les données du client pour obtenir le lien Stripe
      const clientResponse = await axios.get(`/api/clients/${client.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientData(clientResponse.data);

      // Recharger les fichiers pour afficher le bon de commande
      fetchFiles();

      // Recharger les stats et liens Stripe
      fetchStripeLinks();
      fetchPaymentStats();

    } catch (error) {
      console.error('Erreur lors de la génération du bon de commande:', error);
      alert(error.response?.data?.error || 'Erreur lors de la génération du bon de commande');
    } finally {
      setGeneratingBonCommande(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!window.confirm(`Voulez-vous marquer ce client comme payé ?\n\nMontant à enregistrer: ${clientData.montant_bon_commande}€\n\nCette action mettra à jour le statut de paiement.`)) {
      return;
    }

    try {
      setMarkingAsPaid(true);
      const token = localStorage.getItem('token');

      await axios.post(`/api/clients/${clientData.id}/mark-as-paid`, {
        montant_paye: clientData.montant_bon_commande
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mettre à jour les données locales
      const updatedClient = {
        ...clientData,
        paye: true,
        montant_paye: clientData.montant_bon_commande
      };

      setClientData(updatedClient);

      if (onUpdate) {
        onUpdate(updatedClient);
      }

      alert('✅ Client marqué comme payé avec succès !');

      // Rafraîchir les liens Stripe et stats si affichés
      if (showStripeLinks) {
        fetchStripeLinks();
        fetchPaymentStats();
      }
    } catch (error) {
      console.error('Erreur lors du marquage comme payé:', error);
      alert(error.response?.data?.error || 'Erreur lors du marquage comme payé');
    } finally {
      setMarkingAsPaid(false);
    }
  };

  const handleGenerateStripeLink = async () => {
    if (!stripeAmount || parseFloat(stripeAmount) <= 0) {
      alert('Veuillez entrer un montant valide');
      return;
    }

    try {
      setGeneratingStripeLink(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `/api/clients/${clientData.id}/stripe-payment-link`,
        { amount: parseFloat(stripeAmount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGeneratedStripeLink(response.data.payment_link);
      alert('✅ Lien de paiement Stripe généré avec succès !');

      // Rafraîchir les liens Stripe
      fetchStripeLinks();
      fetchPaymentStats();

      // Réinitialiser le formulaire
      setStripeAmount('');

    } catch (error) {
      console.error('Erreur lors de la génération du lien:', error);
      alert(error.response?.data?.error || 'Erreur lors de la génération du lien de paiement');
    } finally {
      setGeneratingStripeLink(false);
    }
  };

  const handleSendSignatureCode = async () => {
    if (!window.confirm(`Envoyer un code de signature électronique à ${clientData.email} ?\n\nLe client recevra un email avec un code unique et un récapitulatif.`)) {
      return;
    }

    try {
      setSendingSignatureCode(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `/api/clients/${clientData.id}/send-signature-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`✅ ${response.data.message}\n\nCode envoyé : ${response.data.code_preview}\nValide jusqu'au : ${new Date(response.data.expires_at).toLocaleDateString('fr-FR')}`);

    } catch (error) {
      console.error('Erreur lors de l\'envoi du code:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'envoi du code de signature');
    } finally {
      setSendingSignatureCode(false);
    }
  };

  const handleValidateSignatureCode = async () => {
    if (!signatureCode.trim()) {
      alert('Veuillez entrer le code de signature');
      return;
    }

    try {
      setValidatingSignatureCode(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `/api/clients/${clientData.id}/validate-signature-code`,
        { code: signatureCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`✅ ${response.data.message}`);

      // Mettre à jour le client localement
      const updatedClient = { ...clientData, client_signe: true };
      setClientData(updatedClient);
      if (onUpdate) onUpdate(updatedClient);

      // Fermer le formulaire et réinitialiser
      setShowSignatureForm(false);
      setSignatureCode('');

      // Rafraîchir l'historique
      fetchHistory();

    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      alert(error.response?.data?.error || 'Erreur lors de la validation du code de signature');
    } finally {
      setValidatingSignatureCode(false);
    }
  };

  const handleDownloadInvoice = async (numeroFacture) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/factures/by-numero/${numeroFacture}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${numeroFacture}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement de la facture:', error);
      alert('Erreur lors du téléchargement de la facture');
    }
  };

  const handleAddInternalNote = async (e) => {
    e.preventDefault();
    if (!newInternalNote.trim()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/clients/${client.id}/internal-notes`,
        { commentaire: newInternalNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInternalNotes([response.data, ...internalNotes]);
      setNewInternalNote('');
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la note interne:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'ajout de la note interne');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSubmittingMessage(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/clients/${client.id}/messages`,
        { commentaire: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages([response.data, ...messages]);
      setNewMessage('');
    } catch (error) {
      console.error('Erreur lors de l\'ajout du message:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'ajout du message');
    } finally {
      setSubmittingMessage(false);
    }
  };

  const handleDeleteInternalNote = async (commentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette note interne ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/clients/${client.id}/internal-notes/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInternalNotes(internalNotes.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Erreur lors de la suppression de la note interne:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression de la note interne');
    }
  };

  const handleDeleteMessage = async (commentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/clients/${client.id}/messages/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(messages.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Erreur lors de la suppression du message:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression du message');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `/api/clients/${client.id}/files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setFiles([response.data, ...files]);
      e.target.value = '';
    } catch (error) {
      console.error('Erreur lors de l\'upload du fichier:', error);
      console.error('Response:', error.response);
      const errorMsg = error.response?.data?.error || error.message || 'Erreur lors de l\'upload du fichier';
      alert('Erreur: ' + errorMsg);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fichier ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/clients/${client.id}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(files.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Erreur lors de la suppression du fichier:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression du fichier');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return <Image size={14} style={{display:'inline',verticalAlign:'middle'}} />;
    if (fileType === 'application/pdf') return <FileDown size={14} style={{display:'inline',verticalAlign:'middle'}} />;
    if (fileType.startsWith('audio/')) return <Music size={14} style={{display:'inline',verticalAlign:'middle'}} />;
    if (fileType.startsWith('video/')) return <Video size={14} style={{display:'inline',verticalAlign:'middle'}} />;
    return <Paperclip size={14} style={{display:'inline',verticalAlign:'middle'}} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDownloadFile = async (filename, originalFilename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/files/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du fichier:', error);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return `${parseFloat(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { text: 'Admin', class: 'badge-admin' },
      agent: { text: 'Agent', class: 'badge-agent' },
      commercial: { text: 'Commercial', class: 'badge-commercial' },
      superviseur: { text: 'Superviseur', class: 'badge-superviseur' },
      superviseur_agent: { text: 'Sup. + Agent', class: 'badge-superviseur-agent' }
    };
    return badges[role] || { text: role, class: 'badge-default' };
  };

  return (
    <div className={isClientView ? "detail-client-view" : "detail-overlay"}>
      <div className="detail-container" onClick={(e) => e.stopPropagation()} style={isClientView ? { maxWidth: '100%', margin: '20px auto', boxShadow: 'none' } : {}}>
        {!isClientView && (
        <div className="detail-header">
          <div>
            <h2>Fiche Client</h2>
            <p className="client-name">{clientData.nom}</p>
          </div>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>
        )}

        <div className="detail-content">
          {/* Informations du client */}
          <section className="detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Informations Générales</h3>
              {isClientView && !editingClient && (
                <button
                  onClick={() => setEditingClient(true)}
                  className="btn-edit-info"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  <Pencil size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Modifier mes informations
                </button>
              )}
              {isClientView && editingClient && (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => {
                      setClientData(client);
                      setEditingClient(false);
                    }}
                    style={{
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.put(
                          `/api/clients/${client.id}`,
                          {
                            nom: clientData.nom,
                            email: clientData.email,
                            telephone: clientData.telephone,
                            entreprise: clientData.entreprise,
                            adresse: clientData.adresse,
                            numero_siret: clientData.numero_siret
                          },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        setEditingClient(false);
                        alert('Vos informations ont été mises à jour avec succès !');
                      } catch (error) {
                        console.error('Erreur lors de la mise à jour:', error);
                        alert('Erreur lors de la mise à jour de vos informations');
                      }
                    }}
                    style={{
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    <Save size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Enregistrer
                  </button>
                </div>
              )}
            </div>
            <div className="info-grid">
              <div className="info-item">
                <label>Numéro client</label>
                <p className="highlight">{clientData.numero_client}</p>
              </div>
              <div className="info-item">
                <label>Nom complet</label>
                {isClientView && editingClient ? (
                  <input
                    type="text"
                    value={clientData.nom}
                    onChange={(e) => setClientData({ ...clientData, nom: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '2px solid #667eea',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                ) : (
                  <p>{clientData.nom}</p>
                )}
              </div>
              <div className="info-item">
                <label>Entreprise</label>
                {isClientView && editingClient ? (
                  <input
                    type="text"
                    value={clientData.entreprise}
                    onChange={(e) => setClientData({ ...clientData, entreprise: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '2px solid #667eea',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                ) : (
                  <p>{clientData.entreprise}</p>
                )}
              </div>
              <div className="info-item">
                <label>Email</label>
                {isClientView && editingClient ? (
                  <input
                    type="email"
                    value={clientData.email}
                    onChange={(e) => setClientData({ ...clientData, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '2px solid #667eea',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                ) : (
                  <p>{clientData.email}</p>
                )}
              </div>
              <div className="info-item">
                <label>Téléphone</label>
                {isClientView && editingClient ? (
                  <input
                    type="tel"
                    value={clientData.telephone}
                    onChange={(e) => setClientData({ ...clientData, telephone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '2px solid #667eea',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                ) : (
                  <p>{clientData.telephone}</p>
                )}
              </div>
              <div className="info-item full-width">
                <label>Adresse</label>
                {isClientView && editingClient ? (
                  <textarea
                    value={clientData.adresse}
                    onChange={(e) => setClientData({ ...clientData, adresse: e.target.value })}
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '2px solid #667eea',
                      borderRadius: '4px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                ) : (
                  <p>{clientData.adresse}</p>
                )}
              </div>
              <div className="info-item">
                <label>Numéro SIRET {!clientData.numero_siret && '(optionnel)'}</label>
                {isClientView && editingClient ? (
                  <input
                    type="text"
                    value={clientData.numero_siret || ''}
                    onChange={(e) => setClientData({ ...clientData, numero_siret: e.target.value })}
                    placeholder="Numéro SIRET (optionnel)"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '2px solid #667eea',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                ) : (
                  <p>{clientData.numero_siret || '-'}</p>
                )}
              </div>

              {/* Identifiants client - Visible uniquement pour les admins */}
              {isAdmin && clientData.client_login && (
                <>
                  <div className="info-item" style={{ borderTop: '2px solid #667eea', paddingTop: '15px', marginTop: '15px' }}>
                    <label style={{ color: '#667eea', fontWeight: '700' }}><Key size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Identifiant (Login)</label>
                    <p style={{ fontWeight: '700', color: '#667eea', fontSize: '16px', fontFamily: 'monospace' }}>
                      {clientData.client_login}
                    </p>
                  </div>
                  <div className="info-item" style={{ borderTop: '2px solid #667eea', paddingTop: '15px', marginTop: '15px' }}>
                    <label style={{ color: '#e74c3c', fontWeight: '700' }}><ShieldCheck size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Mot de passe</label>
                    <p style={{ fontWeight: '700', color: '#e74c3c', fontSize: '16px', fontFamily: 'monospace' }}>
                      {clientData.client_password}
                    </p>
                  </div>
                  <div className="info-item full-width" style={{ background: '#fff3cd', padding: '15px', borderRadius: '8px', border: '1px solid #ffc107', marginTop: '15px' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#856404' }}>
                      <AlertTriangle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> <strong>Information sensible :</strong> Ces identifiants permettent au client d'accéder à son espace personnel.
                    </p>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Informations commerciales */}
          <section className="detail-section">
            <h3>Informations Commerciales</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Montant bon de commande</label>
                <p className="highlight">{formatCurrency(clientData.montant_bon_commande)}</p>
              </div>
              <div className="info-item">
                <label>Temps d'engagement</label>
                <p>{clientData.temps_engagement ? `${clientData.temps_engagement} mois` : '-'}</p>
              </div>
              <div className="info-item">
                <label>Prix mensualité</label>
                <p className="highlight">{formatCurrency(clientData.prix_mensualite)}</p>
              </div>
              <div className="info-item">
                <label>Statut signature</label>
                <p>
                  <span className={`status-badge ${clientData.client_signe ? 'status-yes' : 'status-no'}`}>
                    {clientData.client_signe ? '✓ Signé' : '✗ Non signé'}
                  </span>
                </p>
              </div>

              <div className="info-item">
                <label>Statut paiement</label>
                <p>
                  <span className={`status-badge ${clientData.paye ? 'status-yes' : 'status-no'}`}>
                    {clientData.paye ? '✓ Payé' : '✗ Non payé'}
                  </span>
                  {isAdmin && !clientData.paye && (
                    <button
                      onClick={handleMarkAsPaid}
                      disabled={markingAsPaid}
                      className="btn-mark-paid"
                      style={{
                        marginLeft: '10px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: markingAsPaid ? 'not-allowed' : 'pointer',
                        opacity: markingAsPaid ? 0.6 : 1
                      }}
                    >
                      {markingAsPaid ? '⏳ Marquage...' : '✓ Marquer comme payé'}
                    </button>
                  )}
                </p>
              </div>
              {clientData.paye && clientData.montant_paye && (
                <div className="info-item">
                  <label>Montant payé</label>
                  <p className="highlight" style={{color: '#2e7d32'}}>{formatCurrency(clientData.montant_paye)}</p>
                </div>
              )}
              {clientData.subscription_status && (
                <div className="info-item">
                  <label>Statut abonnement</label>
                  <p>
                    <span className={`status-badge ${
                      clientData.subscription_status === 'active' ? 'status-yes' :
                      clientData.subscription_status === 'canceled' ? 'status-no' :
                      'status-pending'
                    }`}>
                      {clientData.subscription_status === 'active' && '✓ Actif'}
                      {clientData.subscription_status === 'canceled' && '✗ Annulé'}
                      {clientData.subscription_status === 'incomplete' && '⏳ En cours'}
                      {clientData.subscription_status === 'incomplete_expired' && '✗ Expiré'}
                      {clientData.subscription_status === 'trialing' && <><RefreshCw size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Essai</>}
                      {clientData.subscription_status === 'past_due' && <><AlertTriangle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Impayé</>}
                      {!['active', 'canceled', 'incomplete', 'incomplete_expired', 'trialing', 'past_due'].includes(clientData.subscription_status) && clientData.subscription_status}
                    </span>
                  </p>
                </div>
              )}
              {clientData.stripe_customer_id && (
                <div className="info-item">
                  <label>ID Client Stripe</label>
                  <p style={{fontSize: '12px', color: '#666', fontFamily: 'monospace'}}>{clientData.stripe_customer_id}</p>
                </div>
              )}
              {clientData.stripe_subscription_id && (
                <div className="info-item">
                  <label>ID Abonnement Stripe</label>
                  <p style={{fontSize: '12px', color: '#666', fontFamily: 'monospace'}}>{clientData.stripe_subscription_id}</p>
                </div>
              )}
              {isAdmin && clientData.agent_nom && (
                <div className="info-item">
                  <label>Agent Téléphonique</label>
                  <p>{clientData.agent_nom}</p>
                </div>
              )}
              {isAdmin && clientData.commercial_nom && (
                <div className="info-item">
                  <label>Commercial</label>
                  <p>{clientData.commercial_nom}</p>
                </div>
              )}
              {clientData.stripe_payment_link && (
                <div className="info-item full-width">
                  <label>Lien de paiement Stripe</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={clientData.stripe_payment_link}
                      readOnly
                      style={{
                        flex: 1,
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        fontSize: '13px'
                      }}
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(clientData.stripe_payment_link);
                        alert('Lien copié dans le presse-papiers !');
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Copier
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Échéancier de paiement */}
          {!isClientView && (
            <PaymentSchedule
              clientId={client.id}
              token={localStorage.getItem('token')}
              isAdmin={isAdmin}
            />
          )}

          {/* Section Compteur Paiements Stripe */}
          {!isClientView && paymentStats && paymentStats.total_links > 0 && (
            <section className="detail-section payment-stats-section">
              <h3>Suivi des Paiements Stripe</h3>
              <div className="payment-stats-grid">
                <div className="stat-card stat-total">
                  <div className="stat-label">Total liens</div>
                  <div className="stat-value">{paymentStats.total_links}</div>
                </div>
                <div className="stat-card stat-pending">
                  <div className="stat-label">⏳ En attente</div>
                  <div className="stat-value">{paymentStats.pending_count || 0}</div>
                  <div className="stat-amount">{formatCurrency(paymentStats.total_pending_amount)}</div>
                </div>
                <div className="stat-card stat-paid">
                  <div className="stat-label">✓ Payés</div>
                  <div className="stat-value">{paymentStats.paid_count || 0}</div>
                  <div className="stat-amount">{formatCurrency(paymentStats.total_paid_amount)}</div>
                </div>
                <div className="stat-card stat-other">
                  <div className="stat-label">Autres</div>
                  <div className="stat-value">
                    {(paymentStats.expired_count || 0) + (paymentStats.canceled_count || 0)}
                  </div>
                  <div className="stat-subtext">
                    {paymentStats.expired_count > 0 && `${paymentStats.expired_count} expirés`}
                    {paymentStats.expired_count > 0 && paymentStats.canceled_count > 0 && ', '}
                    {paymentStats.canceled_count > 0 && `${paymentStats.canceled_count} annulés`}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Section Historique Liens Stripe */}
          {!isClientView && stripeLinks.length > 0 && (
            <section className="detail-section stripe-links-section">
              <h3 onClick={() => setShowStripeLinks(!showStripeLinks)} style={{ cursor: 'pointer' }}>
                {showStripeLinks ? '▼' : '▶'} Historique des Liens de Paiement Stripe
              </h3>
              {showStripeLinks && (
              <div className="stripe-links-list">
                {loadingStripeLinks ? (
                  <p className="loading-links">Chargement...</p>
                ) : (
                  stripeLinks.map((link) => (
                    <div key={link.id} className="stripe-link-item">
                      <div className="link-header">
                        <div className="link-info">
                          <span className={`link-status status-${link.status}`}>
                            {link.status === 'pending' && '⏳ En attente'}
                            {link.status === 'paid' && '✓ Payé'}
                            {link.status === 'expired' && '⏰ Expiré'}
                            {link.status === 'canceled' && '✗ Annulé'}
                          </span>
                          {link.bon_commande && (
                            <span className="link-bdc">{link.bon_commande}</span>
                          )}
                        </div>
                        <div className="link-amount">{formatCurrency(link.amount)}</div>
                      </div>
                      {link.description && (
                        <p className="link-description">{link.description}</p>
                      )}
                      <div className="link-actions">
                        <a
                          href={link.payment_link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-open-link"
                        >
                          <ExternalLink size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Ouvrir le lien
                        </a>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(link.payment_link_url);
                            alert('Lien copié !');
                          }}
                          className="btn-copy-link"
                        >
                          <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Copier
                        </button>
                        <span className="link-date">
                          Créé le {formatDate(link.created_at)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
              )}
            </section>
          )}

          {/* Section Fichiers */}
          <section className="detail-section files-section">
            <h3 onClick={() => setShowFiles(!showFiles)} style={{ cursor: 'pointer' }}>
              {showFiles ? '▼' : '▶'} Fichiers & Documents
            </h3>
            {showFiles && (
            <>

            {/* Formulaire d'upload de fichier - seulement pour admin/agents */}
            {!isClientView && (
            <div className="file-upload-form">
              <label htmlFor="file-upload" className="btn-upload-file">
                {uploadingFile ? 'Upload en cours...' : <><Paperclip size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Ajouter un fichier</>}
              </label>
              <input
                id="file-upload"
                type="file"
                onChange={handleFileUpload}
                disabled={uploadingFile}
                style={{ display: 'none' }}
              />
              <p className="upload-hint">Photos, PDF, enregistrements téléphoniques (max 50 MB)</p>
            </div>
            )}

            {/* Liste des fichiers */}
            <div className="files-list">
              {loadingFiles ? (
                <p className="loading-files">Chargement des fichiers...</p>
              ) : files.length === 0 ? (
                <p className="no-files">Aucun fichier pour le moment</p>
              ) : (
                files.map((file) => (
                  <div key={file.id} className="file-item">
                    <div className="file-info">
                      <span className="file-icon">{getFileIcon(file.file_type)}</span>
                      <div className="file-details">
                        <a
                          href="#"
                          className="file-name"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDownloadFile(file.filename, file.original_filename);
                          }}
                        >
                          {file.original_filename}
                        </a>
                        <div className="file-meta">
                          <span className="file-size">{formatFileSize(file.file_size)}</span>
                          <span className="file-separator">•</span>
                          <span className="file-uploader">Ajouté par {file.user_nom}</span>
                          <span className="file-separator">•</span>
                          <span className="file-date">{formatDate(file.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    {!isClientView && (isAdmin || file.user_id === clientData.current_user_id) && (
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        className="btn-delete-file"
                        title="Supprimer"
                      >
                        <Trash2 size={14} style={{display:'inline',verticalAlign:'middle'}} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            </>
            )}
          </section>

          {/* Section Factures */}
          <section className="detail-section files-section">
            <h3>Mes Factures</h3>
            <div className="files-list">
              {factures.length === 0 ? (
                <p className="no-files">Aucune facture pour le moment</p>
              ) : (
                factures.map((facture) => (
                  <div key={facture.id} className="file-item">
                    <div className="file-info">
                      <span className="file-icon"><FileDown size={14} style={{display:'inline',verticalAlign:'middle'}} /></span>
                      <div className="file-details">
                        <a
                          href="#"
                          className="file-name"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDownloadInvoice(facture.numero_facture);
                          }}
                        >
                          {facture.numero_facture}
                        </a>
                        <div className="file-meta">
                          <span className="file-size">{facture.montant_ttc} € TTC</span>
                          <span className="file-separator">•</span>
                          <span className="file-uploader">Générée par {facture.generee_par}</span>
                          <span className="file-separator">•</span>
                          <span className="file-date">{new Date(facture.date_emission).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Section Souscriptions / Paiements en ligne (Monetico) */}
          {!isClientView && (
          <section className="detail-section files-section">
            <h3>
              Souscriptions & Paiements en ligne
              {souscriptions.length > 0 && (
                <span style={{ fontSize: '0.85rem', fontWeight: 'normal', marginLeft: '0.5rem', color: '#667eea' }}>
                  · {souscriptions.length} paiement{souscriptions.length > 1 ? 's' : ''}
                  {' · Total : '}
                  {souscriptions
                    .filter(s => s.statut === 'PAYE')
                    .reduce((sum, s) => sum + parseFloat(s.montant || 0), 0)
                    .toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
              )}
            </h3>

            {/* Barre auto-recouvrement (admin) : relance des paiements récurrents refusés */}
            {isAdmin && souscriptions.some(s => s.mode === 'recurrent' && s.statut === 'REFUSE') && (
              <div style={{ marginBottom: '0.6rem', padding: '0.5rem 0.7rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '0.8rem', color: '#92400e' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between' }}>
                  <span>🔁 <strong>Auto-recouvrement actif</strong> — les paiements récurrents refusés sont relancés chaque jour (8h) pendant 30 jours, jusqu'à acceptation.</span>
                  <button onClick={runAutoRecouvrementNow} disabled={autoRunning}
                    style={{ fontSize: '0.75rem', fontWeight: 700, padding: '0.35rem 0.7rem', borderRadius: '6px', border: 'none', color: '#fff', background: '#b45309', cursor: autoRunning ? 'not-allowed' : 'pointer', opacity: autoRunning ? 0.6 : 1 }}>
                    {autoRunning ? 'Relance en cours…' : '▶ Relancer maintenant'}
                  </button>
                </div>
                {autoRun && (
                  <div style={{ marginTop: '0.5rem', color: autoRun.error ? '#991b1b' : '#065f46' }}>
                    {autoRun.error
                      ? `⚠️ ${autoRun.error}`
                      : `✅ ${autoRun.total} traité(s) · ${autoRun.payes} payé(s) · ${autoRun.attente || 0} en attente · ${autoRun.refuses} relance · ${autoRun.arretes} arrêté(s) · ${autoRun.erreurs} erreur(s)`}
                    <button onClick={() => setAutoRun(null)} style={{ marginLeft: '0.5rem', fontSize: '0.7rem', background: 'transparent', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>fermer</button>
                  </div>
                )}
              </div>
            )}

            <div className="files-list">
              {loadingSouscriptions ? (
                <p className="loading-files">Chargement des souscriptions...</p>
              ) : souscriptions.length === 0 ? (
                <p className="no-files">Aucun paiement en ligne pour ce client</p>
              ) : (
                souscriptions.map((s) => (
                  <div key={s.id} className="file-item" style={{
                    borderLeft: `4px solid ${s.statut === 'PAYE' ? '#10b981' : s.statut === 'REFUSE' ? '#ef4444' : '#94a3b8'}`,
                    paddingLeft: '0.75rem',
                    marginBottom: '0.5rem'
                  }}>
                    <div className="file-info" style={{ flex: 1 }}>
                      <div className="file-details" style={{ flex: 1 }}>
                        <div className="file-name" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <span>
                            <strong>{s.service || s.product_name || 'Service'}</strong>
                            {s.mode === 'recurrent' && (
                              <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', padding: '0.15rem 0.5rem', background: '#fef3c7', color: '#92400e', borderRadius: '999px', textTransform: 'uppercase', fontWeight: 700 }}>
                                Récurrent
                              </span>
                            )}
                          </span>
                          <span style={{ fontWeight: 700, color: s.statut === 'PAYE' ? '#059669' : '#475569' }}>
                            {parseFloat(s.montant || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {s.devise || 'EUR'}
                          </span>
                        </div>
                        <div className="file-meta" style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#475569', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <span><strong>Date :</strong> {new Date(s.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          <span className="file-separator">•</span>
                          <span><strong>Réf :</strong> {s.reference}</span>
                          <span className="file-separator">•</span>
                          <span><strong>Statut :</strong>{' '}
                            <span style={{
                              padding: '0.1rem 0.45rem',
                              borderRadius: '4px',
                              background: s.statut === 'PAYE' ? '#d1fae5' : s.statut === 'REFUSE' ? '#fee2e2' : '#e5e7eb',
                              color: s.statut === 'PAYE' ? '#065f46' : s.statut === 'REFUSE' ? '#991b1b' : '#374151',
                              fontWeight: 700, fontSize: '0.75rem'
                            }}>{s.statut}</span>
                          </span>
                          {s.brand && <><span className="file-separator">•</span><span>{s.brand}</span></>}
                          {s.cb_mask && <><span className="file-separator">•</span><span>{s.cb_mask}</span></>}
                          {s.numauto && s.numauto !== '000000' && <><span className="file-separator">•</span><span>Auto: {s.numauto}</span></>}
                          {s.motif_refus && <><span className="file-separator">•</span><span style={{ color: '#dc2626' }}>Refus : {s.motif_refus}</span></>}
                        </div>
                        {(s.cb_origin || s.transaction_origin || s.client_ip) && (
                          <div className="file-meta" style={{ marginTop: '0.15rem', fontSize: '0.75rem', color: '#94a3b8', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                            {s.cb_origin && <span>Carte : {s.cb_origin}</span>}
                            {s.transaction_origin && <span>· Transaction : {s.transaction_origin}</span>}
                            {s.client_ip && <span>· IP : {s.client_ip}</span>}
                            {s.cb_validity && <span>· Exp. {s.cb_validity.slice(0,2)}/{s.cb_validity.slice(2)}</span>}
                          </div>
                        )}

                        {/* Actions Monetico (admin) : recouvrement / résiliation / remboursement / état */}
                        {isAdmin && (
                          <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
                            {(() => {
                              const busy = (t) => opLoading === `${s.id}:${t}`;
                              const anyBusy = !!opLoading;
                              const btn = (bg) => ({
                                fontSize: '0.72rem', fontWeight: 700, padding: '0.3rem 0.6rem', borderRadius: '6px',
                                border: 'none', color: '#fff', background: bg, cursor: anyBusy ? 'not-allowed' : 'pointer', opacity: anyBusy ? 0.6 : 1
                              });
                              return (
                                <>
                                  <button style={btn('#3b82f6')} disabled={anyBusy} onClick={() => runSouscriptionOp(s, 'state')}>
                                    {busy('state') ? '…' : '🔍 Voir l\'état'}
                                  </button>
                                  {s.mode === 'recurrent' && (
                                    <button style={btn('#059669')} disabled={anyBusy} onClick={() => runSouscriptionOp(s, 'capture')}>
                                      {busy('capture') ? '…' : '💶 Recouvrer le paiement'}
                                    </button>
                                  )}
                                  {s.mode === 'recurrent' && (
                                    <button style={btn('#b45309')} disabled={anyBusy} onClick={() => runSouscriptionOp(s, 'stop')}>
                                      {busy('stop') ? '…' : '⛔ Résilier la récurrence'}
                                    </button>
                                  )}
                                  {s.statut === 'PAYE' && (
                                    <button style={btn('#dc2626')} disabled={anyBusy} onClick={() => runSouscriptionOp(s, 'refund')}>
                                      {busy('refund') ? '…' : '↩️ Rembourser'}
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}

                        {/* Suivi auto-recouvrement (souscription récurrente refusée) */}
                        {isAdmin && s.mode === 'recurrent' && s.statut === 'REFUSE' && (() => {
                          const stopped = !!s.recouvrement_stop_reason;
                          const active = !!s.auto_recouvrement && !stopped;
                          const stopLabels = { paye: 'payé', deja_paye: 'déjà payé', annulee: 'annulé', expiree: 'expiré', deadline: '30 j écoulés', desactive_manuel: 'désactivé' };
                          return (
                            <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem',
                              background: active ? '#ecfdf5' : '#f1f5f9', border: `1px solid ${active ? '#a7f3d0' : '#cbd5e1'}`,
                              color: '#334155', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>
                                🔁 Relance auto : <strong>{active ? 'ACTIVE' : 'arrêtée'}</strong>
                                {stopped && stopLabels[s.recouvrement_stop_reason] ? ` (${stopLabels[s.recouvrement_stop_reason]})` : ''}
                                {' · '}{s.recouvrement_attempts || 0} tentative{(s.recouvrement_attempts || 0) > 1 ? 's' : ''}
                                {s.recouvrement_last ? ` · dernière : ${new Date(s.recouvrement_last).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
                              </span>
                              <span style={{ display: 'flex', gap: '0.4rem' }}>
                                <button onClick={() => setReducedAttempt(s)}
                                  style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.25rem 0.55rem', borderRadius: '5px', border: 'none', color: '#fff', background: '#7c3aed', cursor: 'pointer' }}>
                                  {s.recouvrement_jour_reduit != null ? '✎ Tentative réduite' : '+ Tentative réduite'}
                                </button>
                                <button onClick={() => toggleAutoRecouvrement(s)}
                                  style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.25rem 0.55rem', borderRadius: '5px', border: 'none', color: '#fff', background: active ? '#64748b' : '#059669', cursor: 'pointer' }}>
                                  {active ? 'Désactiver' : 'Réactiver'}
                                </button>
                              </span>
                            </div>
                          );
                        })()}

                        {isAdmin && s.mode === 'recurrent' && s.statut === 'REFUSE' && s.recouvrement_jour_reduit != null && (
                          <div style={{ marginTop: '0.3rem', fontSize: '0.72rem', color: '#6d28d9' }}>
                            🎯 Tentative réduite programmée : à partir du <strong>jour {s.recouvrement_jour_reduit}</strong>, tenter <strong>{parseFloat(s.recouvrement_montant_reduit).toFixed(2)} €</strong> (au lieu de {parseFloat(s.montant).toFixed(2)} €)
                          </div>
                        )}

                        {opResult && opResult.souscriptionId === s.id && (
                          <div style={{
                            marginTop: '0.5rem', padding: '0.5rem 0.7rem', borderRadius: '6px', fontSize: '0.78rem',
                            whiteSpace: 'pre-line',
                            background: opResult.ok ? '#d1fae5' : '#fee2e2',
                            color: opResult.ok ? '#065f46' : '#991b1b',
                            border: `1px solid ${opResult.ok ? '#6ee7b7' : '#fca5a5'}`
                          }}>
                            <strong>{opResult.ok ? '✅' : '⚠️'} {opResult.title}</strong>
                            <div>{opResult.message}</div>
                            <button onClick={() => setOpResult(null)} style={{ marginTop: '0.35rem', fontSize: '0.7rem', background: 'transparent', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>fermer</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
          )}

          {/* Section Historique des modifications */}
          {!isClientView && (
          <section className="detail-section history-section">
            <h3 onClick={() => setShowHistory(!showHistory)} style={{ cursor: 'pointer' }}>
              {showHistory ? '▼' : '▶'} Historique des Modifications
            </h3>
            {showHistory && (
            <div className="history-list">
              {loadingHistory ? (
                <p className="loading-history">Chargement de l'historique...</p>
              ) : history.length === 0 ? (
                <p className="no-history">Aucune modification enregistrée</p>
              ) : (
                history.map((entry) => (
                  <div key={entry.id} className="history-item">
                    <div className="history-header">
                      <div className="history-action">
                        <span className={`action-badge action-${entry.action}`}>
                          {entry.action === 'modification' && <><Pencil size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Modification</>}
                          {entry.action === 'paiement' && <><CreditCard size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Paiement</>}
                          {entry.action === 'abonnement_cree' && <><Calendar size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Abonnement créé</>}
                          {entry.action === 'abonnement_mis_a_jour' && <><Calendar size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Abonnement mis à jour</>}
                          {entry.action === 'abonnement_annule' && <><XCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Abonnement annulé</>}
                          {!['modification', 'paiement', 'abonnement_cree', 'abonnement_mis_a_jour', 'abonnement_annule'].includes(entry.action) && entry.action}
                        </span>
                      </div>
                      <div className="history-meta">
                        <span className="history-user">{entry.user_nom || 'Système'}</span>
                        <span className="history-separator">•</span>
                        <span className="history-date">{formatDate(entry.created_at)}</span>
                      </div>
                    </div>
                    <p className="history-description">{entry.description}</p>
                    {entry.field_name && entry.old_value && entry.new_value && (
                      <div className="history-change">
                        <span className="change-old">{entry.old_value}</span>
                        <span className="change-arrow">→</span>
                        <span className="change-new">{entry.new_value}</span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            )}
          </section>
          )}

          {/* Section Commentaires & Notes internes (équipe uniquement) */}
          {!isClientView && (
            <section className="detail-section comments-section">
              <h3><FileText size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Commentaires & Notes internes</h3>
              <p className="section-info">Cette section est uniquement visible par l'équipe. Le client ne voit jamais ces notes.</p>

              {/* Formulaire d'ajout de note interne */}
              <form onSubmit={handleAddInternalNote} className="comment-form">
                <textarea
                  value={newInternalNote}
                  onChange={(e) => setNewInternalNote(e.target.value)}
                  placeholder="Ajouter une note interne (visible uniquement par l'équipe)..."
                  rows="3"
                  disabled={submitting}
                />
                <button
                  type="submit"
                  className="btn-add-comment"
                  disabled={submitting || !newInternalNote.trim()}
                >
                  {submitting ? 'Ajout...' : 'Ajouter une note interne'}
                </button>
              </form>

              {/* Liste des notes internes */}
              <div className="comments-list">
                {internalNotes.length === 0 ? (
                  <p className="no-comments">Aucune note interne pour le moment</p>
                ) : (
                  internalNotes.map((note) => (
                    <div key={note.id} className="comment-item comment-internal">
                      <div className="comment-header">
                        <div className="comment-author">
                          <strong>{note.user_nom}</strong>
                          <span className={`role-badge-small ${getRoleBadge(note.user_role).class}`}>
                            {getRoleBadge(note.user_role).text}
                          </span>
                        </div>
                        <div className="comment-actions">
                          <span className="comment-date">{formatDate(note.created_at)}</span>
                          {(isAdmin || note.user_id === clientData.current_user_id) && (
                            <button
                              onClick={() => handleDeleteInternalNote(note.id)}
                              className="btn-delete-comment"
                              title="Supprimer"
                            >
                              <Trash2 size={14} style={{display:'inline',verticalAlign:'middle'}} />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="comment-text">{note.commentaire}</p>
                    </div>
                  ))
                )}
              </div>
            </section>
          )}

          {/* Section Messagerie Client (visible par le client et l'équipe) */}
          <section className="detail-section comments-section">
            <h3><MessageSquare size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {isClientView ? 'Messagerie' : 'Messagerie Client'}</h3>
            {!isClientView && (
              <p className="section-info">Cette section est visible par le client. Utilisez-la pour communiquer avec lui.</p>
            )}

            {/* Formulaire d'ajout de message */}
            <form onSubmit={handleAddMessage} className="comment-form">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={isClientView ? "Écrivez votre message..." : "Écrire un message au client..."}
                rows="3"
                disabled={submittingMessage}
              />
              <button
                type="submit"
                className="btn-add-comment"
                disabled={submittingMessage || !newMessage.trim()}
              >
                {submittingMessage ? 'Envoi...' : 'Envoyer'}
              </button>
            </form>

            {/* Liste des messages */}
            <div className="comments-list">
              {messages.length === 0 ? (
                <p className="no-comments">Aucun message pour le moment</p>
              ) : (
                messages.map((message) => (
                  <div key={message.id} className={`comment-item ${message.user_role === 'client' ? 'comment-client' : 'comment-admin'}`}>
                    <div className="comment-header">
                      <div className="comment-author">
                        <strong>{message.user_nom}</strong>
                        {!isClientView && (
                          <span className={`role-badge-small ${getRoleBadge(message.user_role).class}`}>
                            {getRoleBadge(message.user_role).text}
                          </span>
                        )}
                      </div>
                      <div className="comment-actions">
                        <span className="comment-date">{formatDate(message.created_at)}</span>
                        {(isAdmin || message.user_id === clientData.current_user_id) && (
                          <button
                            onClick={() => handleDeleteMessage(message.id)}
                            className="btn-delete-comment"
                            title="Supprimer"
                          >
                            <Trash2 size={14} style={{display:'inline',verticalAlign:'middle'}} />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="comment-text">{message.commentaire}</p>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        <div className="detail-footer">
          {!isClientView && (
          <button onClick={onClose} className="btn-close-detail">
            Fermer
          </button>
          )}
          {!isClientView && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '10px',
            width: '100%'
          }}>
            {/* Si client signé et utilisateur non-admin : afficher seulement le lien de paiement */}
            {clientData.client_signe && !isAdmin ? (
              <button
                onClick={() => setShowStripeForm(true)}
                className="btn-generate-stripe-link"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  whiteSpace: 'normal',
                  lineHeight: '1.3',
                  gridColumn: '1 / -1'
                }}
              >
                <CreditCard size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Générer lien de paiement
              </button>
            ) : (
              <>
                <button
                  onClick={handleGenerateBonCommande}
                  className="btn-generate-bon-commande"
                  disabled={generatingBonCommande}
                  style={{
                    padding: '12px 16px',
                    fontSize: '13px',
                    whiteSpace: 'normal',
                    lineHeight: '1.3'
                  }}
                >
                  {generatingBonCommande ? 'Génération...' : <><Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Bon de commande</>}
                </button>
                {isAdmin && clientData.paye && clientData.montant_paye && (
                  <button
                    onClick={handleGenerateInvoice}
                    className="btn-generate-invoice"
                    disabled={generatingInvoice}
                    style={{
                      padding: '12px 16px',
                      fontSize: '13px',
                      whiteSpace: 'normal',
                      lineHeight: '1.3'
                    }}
                  >
                    {generatingInvoice ? 'Génération...' : <><FileDown size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Générer Facture</>}
                  </button>
                )}
                {(isAdmin || isCommercial) && (
                  <button
                    onClick={() => setShowStripeForm(true)}
                    className="btn-generate-stripe-link"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      whiteSpace: 'normal',
                      lineHeight: '1.3'
                    }}
                  >
                    <CreditCard size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Lien Stripe
                  </button>
                )}
                {(isAdmin || isCommercial) && (
                  <button
                    onClick={() => { setShowMoneticoModal(true); setMoneticoLink(null); setMoneticoCopied(false); }}
                    className="btn-generate-monetico-link"
                    style={{
                      background: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      whiteSpace: 'normal',
                      lineHeight: '1.3'
                    }}
                  >
                    <CreditCard size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Lien Monetico
                  </button>
                )}
                {(isAdmin || isCommercial) && !clientData.client_signe && (
                  <button
                    onClick={() => setShowSignatureForm(true)}
                    className="btn-signature"
                    style={{
                      background: 'linear-gradient(135deg, #FF6B6B 0%, #EE5A6F 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      whiteSpace: 'normal',
                      lineHeight: '1.3'
                    }}
                  >
                    <PenLine size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Signature électronique
                  </button>
                )}
                <button
                  onClick={() => {
                    onClose();
                    onUpdate(clientData);
                  }}
                  className="btn-edit-detail"
                  style={{
                    padding: '12px 16px',
                    fontSize: '13px',
                    whiteSpace: 'normal',
                    lineHeight: '1.3'
                  }}
                >
                  Modifier le client
                </button>
              </>
            )}
          </div>
          )}
        </div>

        {/* Modal pour générer un lien de paiement Stripe */}
        {showStripeForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '600px',
                width: '90%',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h2 style={{ margin: 0, color: '#667eea', fontSize: '24px' }}>
                  <CreditCard size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Générer un lien de paiement Stripe
                </h2>
                <button onClick={() => { setShowStripeForm(false); setStripeAmount(''); setGeneratedStripeLink(null); }} style={{ background: 'none', border: 'none', fontSize: '32px', color: '#999', cursor: 'pointer', lineHeight: 1, padding: 0 }}>&times;</button>
              </div>
              <p style={{ margin: '0 0 25px 0', color: '#666', fontSize: '14px' }}>
                Frais de dossier : Création de vos comptes sur les différentes plateformes, achat des noms de domaine et envoi administratif de vos dossiers
              </p>

              {!generatedStripeLink ? (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '15px', fontWeight: '600', color: '#333' }}>
                      Montant à payer (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={stripeAmount}
                      onChange={(e) => setStripeAmount(e.target.value)}
                      placeholder="Ex: 350"
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #667eea',
                        borderRadius: '8px',
                        fontSize: '18px',
                        fontWeight: '600',
                        outline: 'none'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => {
                        setShowStripeForm(false);
                        setStripeAmount('');
                      }}
                      style={{
                        padding: '12px 24px',
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600'
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleGenerateStripeLink}
                      disabled={generatingStripeLink}
                      style={{
                        padding: '12px 24px',
                        background: generatingStripeLink ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: generatingStripeLink ? 'not-allowed' : 'pointer',
                        fontSize: '15px',
                        fontWeight: '600'
                      }}
                    >
                      {generatingStripeLink ? '⏳ Génération en cours...' : '✓ Générer le lien'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{
                    background: '#d4edda',
                    border: '2px solid #28a745',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '20px'
                  }}>
                    <p style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: '#155724' }}>
                      <CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Lien généré avec succès !
                    </p>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                      <input
                        type="text"
                        value={generatedStripeLink}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '10px',
                          border: '1px solid #28a745',
                          borderRadius: '6px',
                          fontSize: '14px',
                          background: 'white'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(generatedStripeLink);
                          alert('Lien copié dans le presse-papiers !');
                        }}
                        style={{
                          flex: 1,
                          padding: '10px 20px',
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Copier le lien
                      </button>
                      <a
                        href={generatedStripeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          flex: 1,
                          padding: '10px 20px',
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          textDecoration: 'none',
                          textAlign: 'center'
                        }}
                      >
                        <ExternalLink size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Ouvrir le lien
                      </a>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowStripeForm(false);
                      setStripeAmount('');
                      setGeneratedStripeLink(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 24px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Fermer
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ============== MODAL : Lien de paiement Monetico ============== */}
        {showMoneticoModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(8px)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
          }} onClick={(e) => { if (e.target === e.currentTarget) setShowMoneticoModal(false); }}>
            <div style={{
              background: 'white', borderRadius: '20px', width: '100%', maxWidth: '560px',
              boxShadow: '0 30px 80px -20px rgba(0,0,0,0.5)', overflow: 'hidden', position: 'relative'
            }}>
              <div style={{
                background: 'linear-gradient(135deg,#00f2fe 0%,#4facfe 50%,#667eea 100%)',
                padding: '1.5rem', color: 'white', position: 'relative'
              }}>
                <button onClick={() => setShowMoneticoModal(false)}
                  style={{ position: 'absolute', top: '0.85rem', right: '0.85rem',
                    background: 'rgba(255,255,255,0.2)', border: 'none', width: '32px', height: '32px',
                    borderRadius: '50%', color: 'white', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
                  Générer un lien de paiement Monetico
                </h3>
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.92rem', opacity: 0.9 }}>
                  {clientData.nom} · {clientData.email}
                </p>
              </div>

              <div style={{ padding: '1.75rem' }}>
                {!moneticoLink ? (
                  <>
                    {/* Type */}
                    <div style={{ marginBottom: '1.25rem' }}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.55rem' }}>
                        Type de paiement
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                        {[['immediat', 'Paiement unique', '💳'], ['mensuel', 'Abonnement mensuel', '🔄']].map(([val, lbl, ic]) => (
                          <button key={val} type="button" onClick={() => setMoneticoType(val)}
                            style={{
                              padding: '0.85rem 0.5rem',
                              border: `2px solid ${moneticoType === val ? '#4facfe' : '#e2e8f0'}`,
                              background: moneticoType === val ? 'linear-gradient(135deg,#eef2ff,#dbeafe)' : 'white',
                              borderRadius: '10px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700,
                              color: '#0f172a', transition: 'all 0.2s', textAlign: 'center'
                            }}>
                            <div style={{ fontSize: '1.3rem', marginBottom: '0.2rem' }}>{ic}</div>
                            {lbl}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Montant */}
                    <div style={{ marginBottom: '1.25rem' }}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.55rem' }}>
                        Montant TTC {moneticoType === 'mensuel' && '(par mois)'}
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input type="number" value={moneticoAmount} onChange={(e) => setMoneticoAmount(e.target.value)}
                          min="1" step="0.01" placeholder="149.00" autoFocus
                          style={{ width: '100%', padding: '1rem 3.5rem 1rem 1rem', fontSize: '1.5rem',
                            fontWeight: 800, border: '2px solid #e2e8f0', borderRadius: '12px', outline: 'none' }} />
                        <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
                          color: '#64748b', fontWeight: 700 }}>EUR</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
                        {[89, 149, 199, 349, 979].map(v => (
                          <button key={v} type="button" onClick={() => setMoneticoAmount(String(v))}
                            style={{ padding: '0.3rem 0.8rem', border: '1.5px solid #e2e8f0', background: '#f8fafc',
                              borderRadius: '999px', fontSize: '0.82rem', fontWeight: 700, color: '#475569', cursor: 'pointer' }}>
                            {v} €
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Dossier (préfilled) */}
                    <div style={{ marginBottom: '1.25rem' }}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.4rem' }}>
                        N° de dossier (optionnel)
                      </label>
                      <input type="text" value={moneticoDossier} onChange={(e) => setMoneticoDossier(e.target.value)}
                        placeholder={`CLIENT-${clientData.id}`}
                        style={{ width: '100%', padding: '0.75rem 1rem', fontSize: '0.95rem',
                          border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none' }} />
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.4rem' }}>
                        Objet du paiement (optionnel)
                      </label>
                      <input type="text" value={moneticoDescription} onChange={(e) => setMoneticoDescription(e.target.value)}
                        placeholder="Acompte projet site web — facture #2026-042"
                        style={{ width: '100%', padding: '0.75rem 1rem', fontSize: '0.95rem',
                          border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none' }} />
                    </div>

                    <button
                      onClick={async () => {
                        if (!moneticoAmount || parseFloat(moneticoAmount) < 1) {
                          alert('Saisissez un montant valide (minimum 1 €).');
                          return;
                        }
                        setMoneticoGenerating(true);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await axios.post(`/api/clients/${clientData.id}/payment-link`, {
                            type: moneticoType,
                            amount: parseFloat(moneticoAmount),
                            dossier: moneticoDossier || null,
                            description: moneticoDescription || null
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          setMoneticoLink(res.data);
                        } catch (err) {
                          alert('Erreur : ' + (err.response?.data?.error || err.message));
                        } finally {
                          setMoneticoGenerating(false);
                        }
                      }}
                      disabled={moneticoGenerating}
                      style={{
                        width: '100%', padding: '1rem', background: 'linear-gradient(135deg,#00f2fe,#4facfe,#667eea)',
                        color: 'white', border: 'none', borderRadius: '12px', fontWeight: 800, fontSize: '1rem',
                        cursor: moneticoGenerating ? 'wait' : 'pointer',
                        boxShadow: '0 15px 35px -10px rgba(79,172,254,0.55)', opacity: moneticoGenerating ? 0.6 : 1
                      }}>
                      {moneticoGenerating ? 'Génération…' : 'Générer le lien'}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Lien généré */}
                    <div style={{ background: 'linear-gradient(135deg,#ecfdf5,#d1fae5)',
                      border: '1.5px solid #10b981', borderRadius: '12px', padding: '0.85rem 1rem',
                      marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ fontSize: '1.4rem' }}>✅</span>
                      <div>
                        <div style={{ fontWeight: 800, color: '#065f46' }}>Lien généré</div>
                        <div style={{ fontSize: '0.85rem', color: '#047857' }}>
                          {parseFloat(moneticoLink.amount).toLocaleString('fr-FR', {minimumFractionDigits:2})} €
                          {moneticoLink.type === 'mensuel' && ' / mois (récurrent)'}
                        </div>
                      </div>
                    </div>

                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.4rem' }}>
                      Lien de paiement
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
                      <input type="text" value={moneticoLink.url} readOnly onClick={(e) => e.target.select()}
                        style={{ flex: 1, padding: '0.75rem 1rem', fontSize: '0.85rem',
                          border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none',
                          background: '#f8fafc', fontFamily: 'monospace' }} />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(moneticoLink.url);
                          setMoneticoCopied(true);
                          setTimeout(() => setMoneticoCopied(false), 2000);
                        }}
                        style={{ padding: '0 1rem', background: moneticoCopied ? '#10b981' : '#0f172a',
                          color: 'white', border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer',
                          fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        {moneticoCopied ? '✓ Copié' : 'Copier'}
                      </button>
                    </div>

                    {/* Actions d'envoi */}
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.5rem' }}>
                      Envoyer au client
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1.25rem' }}>
                      <button
                        onClick={async () => {
                          if (moneticoSending) return;
                          setMoneticoSending('email'); setMoneticoSent(null);
                          try {
                            const token = localStorage.getItem('token');
                            await axios.post(
                              `/api/clients/${clientData.id}/send-payment-link-email`,
                              { url: moneticoLink.url, amount: moneticoLink.amount, type: moneticoLink.type },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setMoneticoSent('email');
                            setTimeout(() => setMoneticoSent(null), 4000);
                          } catch (err) {
                            alert('Erreur envoi email : ' + (err.response?.data?.error || err.message));
                          } finally { setMoneticoSending(null); }
                        }}
                        disabled={moneticoSending !== null}
                        style={{ padding: '0.75rem', background: moneticoSent === 'email' ? '#d1fae5' : '#f8fafc',
                          border: `1.5px solid ${moneticoSent === 'email' ? '#10b981' : '#e2e8f0'}`,
                          borderRadius: '10px', textAlign: 'center',
                          color: '#0f172a', fontWeight: 600, fontSize: '0.82rem',
                          cursor: moneticoSending ? 'wait' : 'pointer', opacity: moneticoSending === 'email' ? 0.7 : 1 }}>
                        {moneticoSending === 'email' ? '⏳ Envoi…' : moneticoSent === 'email' ? '✓ Envoyé' : '✉ Email'}
                      </button>
                      {clientData.telephone && (
                        <>
                          <button
                            onClick={async () => {
                              if (moneticoSending) return;
                              setMoneticoSending('sms'); setMoneticoSent(null);
                              try {
                                const token = localStorage.getItem('token');
                                await axios.post(
                                  `/api/clients/${clientData.id}/send-payment-link-sms`,
                                  { url: moneticoLink.url, amount: moneticoLink.amount, type: moneticoLink.type },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                setMoneticoSent('sms');
                                setTimeout(() => setMoneticoSent(null), 4000);
                              } catch (err) {
                                alert('Erreur envoi SMS : ' + (err.response?.data?.error || err.message));
                              } finally { setMoneticoSending(null); }
                            }}
                            disabled={moneticoSending !== null}
                            style={{ padding: '0.75rem', background: moneticoSent === 'sms' ? '#d1fae5' : '#f8fafc',
                              border: `1.5px solid ${moneticoSent === 'sms' ? '#10b981' : '#e2e8f0'}`,
                              borderRadius: '10px', textAlign: 'center',
                              color: '#0f172a', fontWeight: 600, fontSize: '0.82rem',
                              cursor: moneticoSending ? 'wait' : 'pointer', opacity: moneticoSending === 'sms' ? 0.7 : 1 }}>
                            {moneticoSending === 'sms' ? '⏳ Envoi…' : moneticoSent === 'sms' ? '✓ Envoyé' : '📱 SMS'}
                          </button>
                          <a href={`https://wa.me/${clientData.telephone.replace(/\D/g,'').replace(/^0/, '33')}?text=${encodeURIComponent(`Bonjour ${clientData.nom}, votre lien de paiement : ${moneticoLink.url}`)}`}
                            target="_blank" rel="noreferrer"
                            style={{ padding: '0.75rem', background: '#f8fafc', border: '1.5px solid #e2e8f0',
                              borderRadius: '10px', textAlign: 'center', textDecoration: 'none',
                              color: '#0f172a', fontWeight: 600, fontSize: '0.82rem' }}>
                            💬 WhatsApp
                          </a>
                        </>
                      )}
                    </div>

                    {/* QR Code */}
                    <details style={{ marginBottom: '1rem', background: '#f8fafc',
                      borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                      <summary style={{ padding: '0.75rem 1rem', cursor: 'pointer', fontWeight: 700,
                        fontSize: '0.88rem', color: '#475569' }}>📲 Afficher le QR code</summary>
                      <div style={{ padding: '1rem', textAlign: 'center' }}>
                        <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(moneticoLink.url)}`}
                          alt="QR code" style={{ borderRadius: '8px' }} />
                        <p style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: '#64748b' }}>
                          Le client scanne avec son téléphone pour payer
                        </p>
                      </div>
                    </details>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => { setMoneticoLink(null); setMoneticoAmount(''); setMoneticoDossier(''); setMoneticoDescription(''); }}
                        style={{ flex: 1, padding: '0.85rem', background: 'white', border: '1.5px solid #cbd5e1',
                          borderRadius: '10px', fontWeight: 700, cursor: 'pointer', color: '#475569' }}>
                        Générer un autre lien
                      </button>
                      <button
                        onClick={() => setShowMoneticoModal(false)}
                        style={{ flex: 1, padding: '0.85rem', background: '#0f172a', color: 'white',
                          border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}>
                        Fermer
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal pour la signature électronique */}
        {showSignatureForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '600px',
                width: '90%',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h2 style={{ margin: 0, color: '#FF6B6B', fontSize: '24px' }}>
                  <PenLine size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Signature Électronique
                </h2>
                <button onClick={() => { setShowSignatureForm(false); setSignatureCode(''); }} style={{ background: 'none', border: 'none', fontSize: '32px', color: '#999', cursor: 'pointer', lineHeight: 1, padding: 0 }}>&times;</button>
              </div>
              <p style={{ margin: '0 0 25px 0', color: '#666', fontSize: '14px' }}>
                Choisissez une action pour gérer la signature électronique du client
              </p>

              {/* Deux options : Envoyer code ou Valider code */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '25px' }}>
                {/* Option 1 : Envoyer le code par email */}
                <button
                  onClick={async () => {
                    setShowSignatureForm(false);
                    await handleSendSignatureCode();
                  }}
                  disabled={sendingSignatureCode}
                  style={{
                    padding: '20px',
                    background: sendingSignatureCode ? '#f0f0f0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: sendingSignatureCode ? '#999' : 'white',
                    border: sendingSignatureCode ? '2px solid #ddd' : 'none',
                    borderRadius: '10px',
                    cursor: sendingSignatureCode ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    textAlign: 'left',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}
                >
                  <div style={{ fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Mail size={20} style={{display:'inline',verticalAlign:'middle'}} />
                    {sendingSignatureCode ? '⏳ Envoi en cours...' : 'Envoyer le code de signature'}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9 }}>
                    Le client recevra un email avec un code unique et un récapitulatif complet
                  </div>
                </button>

                {/* Option 2 : Entrer et valider le code */}
                <div style={{
                  padding: '20px',
                  background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                  color: 'white',
                  borderRadius: '10px'
                }}>
                  <div style={{ fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <ShieldCheck size={20} style={{display:'inline',verticalAlign:'middle'}} />
                    <span style={{ fontWeight: '600' }}>Valider le code de signature</span>
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '15px' }}>
                    Entrez le code que le client a reçu par email
                  </div>
                  <input
                    type="text"
                    value={signatureCode}
                    onChange={(e) => setSignatureCode(e.target.value.toUpperCase())}
                    placeholder="Ex: A1B2C3D4"
                    maxLength={8}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '18px',
                      fontWeight: '700',
                      letterSpacing: '3px',
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      outline: 'none',
                      marginBottom: '12px'
                    }}
                  />
                  <button
                    onClick={handleValidateSignatureCode}
                    disabled={validatingSignatureCode || !signatureCode.trim()}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: (validatingSignatureCode || !signatureCode.trim()) ? '#ccc' : 'white',
                      color: (validatingSignatureCode || !signatureCode.trim()) ? '#999' : '#44A08D',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (validatingSignatureCode || !signatureCode.trim()) ? 'not-allowed' : 'pointer',
                      fontSize: '15px',
                      fontWeight: '700'
                    }}
                  >
                    {validatingSignatureCode ? '⏳ Validation...' : '✓ Valider maintenant'}
                  </button>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowSignatureForm(false);
                  setSignatureCode('');
                }}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default ClientDetail;
